import {
  useController,
  type Control,
  type FieldValues,
  type FieldPath,
  type RegisterOptions,
} from "react-hook-form";
import { useTheme } from "../../contexts/ThemeContext";

type Props<T extends FieldValues> = {
  control: Control<T>;
  name: FieldPath<T>;
  label?: string;
  placeholder?: string;
  type?: "text" | "number" | "password";
  autoFocus?: boolean;
  autoComplete?: string;
  rules?: RegisterOptions<T, FieldPath<T>>;
};

/**
 * Champ texte factorisé, branché sur react-hook-form (via useController) et sur le
 * thème `t`. Gère label, valeur, et affichage de l'erreur de validation.
 * Même pattern que OneToOne : un nouveau type d'input = un nouveau composant ici,
 * pas de <input> brut dans les dialogs.
 */
export function TextField<T extends FieldValues>({
  control, name, label, placeholder, type = "text", autoFocus, autoComplete, rules,
}: Props<T>) {
  const { t } = useTheme();
  const { field, fieldState } = useController({ control, name, rules });
  const error = fieldState.error?.message;

  return (
    <div>
      {label && (
        <label
          htmlFor={field.name}
          style={{ display: "block", fontSize: 12, color: t.text2, marginBottom: 6, fontWeight: 500 }}
        >
          {label}{rules?.required && <span style={{ color: t.lateColor }}> *</span>}
        </label>
      )}
      <input
        id={field.name}
        name={field.name}
        ref={field.ref}
        value={field.value ?? ""}
        onChange={field.onChange}
        onBlur={field.onBlur}
        type={type}
        placeholder={placeholder}
        autoFocus={autoFocus}
        autoComplete={autoComplete}
        style={{
          width: "100%", padding: "10px 12px", borderRadius: 8,
          border: `1px solid ${error ? t.lateColor : t.inputBorder}`,
          background: t.inputBg, color: t.text1,
          fontSize: 14, fontFamily: "inherit", outline: "none",
        }}
      />
      {error && (
        <div style={{ fontSize: 11.5, color: t.lateColor, marginTop: 5 }}>{error}</div>
      )}
    </div>
  );
}
